<template>
    <div>
        <!-- Header Card -->
        <div class="card">
            <div class="p-2" style="background:#DBE7F2">
                <TitleButton
                    btnTitle="Add New"
                    :showBtn="true"
                    :showSettingBtn="false"
                    :showAddNew="false"
                    :title="headerTitle"
                    @onClickCloseButton="onClickCloseButton"
                />

                <div class="row mt-1 gy-1">
                    <div class="col-sm-3">
                        <label class="align-middle">Supplier</label>
                        <v-select
                            placeholder="Select Supplier"
                            v-model="formData.supplier_id"
                            :options="contacts"
                            label="name"
                            :reduce="name => name.id"
                        />
                    </div>

                    <div v-if="!isPOCreate" class="col-sm-3">
                        <label class="align-middle form-label">PO No</label>
                        <input
                            type="text"
                            class="form-control"
                            :value="formData.po_number"
                            readonly
                        >
                    </div>

                    <div class="col-sm-3">
                        <label class="align-middle form-label">PO Date</label>
                        <input
                            type="date"
                            class="form-control"
                            v-model="formData.po_date"
                        >
                    </div>

                    <div class="col-sm-3">
                        <label class="form-label">Project</label>
                        <v-select
                            placeholder="Select Project"
                            v-model="formData.project_id"
                            :options="projects"
                            label="name"
                            :reduce="name => name.id"
                        />
                    </div>

                    <div class="col-sm-3">
                        <label class="align-middle form-label">Req Reference</label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="formData.description"
                        >
                    </div>
                </div>
            </div>
        </div>

        <!-- Products -->
        <div class="card mt-2">
            <div class="px-3">
                <ProductForm
                    ref="productForm"
                    @handleProductAdd="handleProductAdd"
                />

                <ProductsTable
                    :itemDetails="formData.item_details"
                    @removeBill="removeBill"
                />
            </div>

            <!-- Totals (UNCHANGED STRUCTURE) -->
            <div class="px-2 mb-3">
                <div class="row mt-3 px-2 justify-content-end">
                    <div class="col-md-5">

                        <div class="row mt-1">
                            <div class="col-md-4">SubTotal :</div>
                            <div class="col-md-8">
                                <input class="form-control text-end" :value="subTotal" readonly />
                            </div>
                        </div>

                        <div class="row mt-1">
                            <div class="col-md-4">(-) Discount / Round up Adj :</div>
                            <div class="col-md-8">
                                <input
                                    class="form-control text-end"
                                    v-model.number="formData.adjustment_amount"
                                />
                            </div>
                        </div>

                        <div class="row mt-1">
                            <div class="col-md-4">(+) Carrying Cost :</div>
                            <div class="col-md-8">
                                <input
                                    class="form-control text-end"
                                    v-model.number="formData.carrying_cost"
                                />
                            </div>
                        </div>

                        <div class="row mt-1">
                            <div class="col-md-4">(+) TDS :</div>
                            <div class="col-md-8">
                                <input
                                    class="form-control text-end"
                                    v-model.number="formData.tds"
                                />
                            </div>
                        </div>

                        <div class="row mt-1 fw-bold">
                            <div class="col-md-4">NET PAYABLE AMOUNT :</div>
                            <div class="col-md-8">
                                <input
                                    class="form-control text-end fw-bold"
                                    :value="grandTotal"
                                    readonly
                                />
                            </div>
                        </div>

                    </div>
                </div>

                <div class="row mb-1 d-flex justify-content-end">
                    <div class="col-2">
                        <button
                            class="btn btn-primary form-control mt-3"
                            @click="savePO"
                            :disabled="loader"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <view-approval-matrix-details
            ref="viewApprovalMatrix"
            :workflowDetails="workflowDetails"
            :approvalLogs="formData.workflow_approval_logs ?? []"
        />

        <GlobalLoader v-if="loader" />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from "vue";
import { useRoute, useRouter } from "vue-router";

import TitleButton from "@/components/atom/TitleButton";
import ProductForm from "@/views/page/company/core-accounting/inventory/purchase-order/ProductForm.vue";
import ProductsTable from "@/views/page/company/core-accounting/inventory/purchase-order/ProductsTable.vue";
import GlobalLoader from "@/components/atom/GlobalLoader.vue";
import ViewApprovalMatrixDetails from "@/components/molecule/approval-matrix/ViewApprovalMatrixDetails.vue";

import handleRequisitions from "@/services/modules/procurement/requisition";
import handleContact from "@/services/modules/contact";
import handleWorkflow from "@/services/modules/approvalMatrix/workflowMatrix";
import handleProjects from "@/services/modules/procurement/project";
import handleHospital from "@/services/modules/hospital";

const route = useRoute();
const router = useRouter();

const showError = inject("showError");
const showSuccess = inject("showSuccess");

const { fetchSinglePO, fetchPONumber, createNewPO, updatePOItem } = handleRequisitions();
const { fetchContactProfiles } = handleContact();
const { fetchWorkflowFromPageId } = handleWorkflow();
const { fetchProjects } = handleProjects();
const { fetchProductsByIds } = handleHospital();

const productForm = ref(null);
const supplier = ref(null);
const loader = ref(false);

const headerTitle = ref("Create Purchase Order");
const isPOCreate = ref(true);
const poEditData = ref({});

const contacts = ref([]);
const projects = ref([]);
const workflowDetails = ref({});

const formData = ref({
    supplier_id: null,
    po_number: null,
    po_date: null,
    adjustment_amount: 0,
    carrying_cost: 0,
    tds: 0,
    item_details: [],
});

/* ================= TOTALS ================= */

const subTotal = computed(() => {
    return formData.value.item_details.reduce((sum, item) => {
        const qty = Number(item.quantity) || 0;
        const rate = Number(item.rate) || 0;
        return sum + qty * rate;
    }, 0).toFixed(2);
});

const grandTotal = computed(() => {
    const subtotal = Number(subTotal.value);
    const discount = Number(formData.value.adjustment_amount) || 0;
    const carrying = Number(formData.value.carrying_cost) || 0;
    const tds = Number(formData.value.tds) || 0;

    return (subtotal - discount + carrying + tds).toFixed(2);
});

/* ================= ITEMS ================= */

const handleProductAdd = (item, index) => {
    if (index !== null && formData.value.item_details[index]) {
        formData.value.item_details[index] = item;
    } else {
        formData.value.item_details.push(item);
    }
};

const removeBill = (index) => {
    formData.value.item_details.splice(index, 1);
};

/* ================= SAVE ================= */

const savePO = async () => {
    if (!formData.value.supplier_id) {
        showError("Supplier is required");
        return;
    }

    const payload = {
        company_id: route.params.companyId,
        supplier_id: formData.value.supplier_id,
        project_id: formData.value.project_id,
        po_number: formData.value.po_number,
        po_type: "new_po",
        po_date: formData.value.po_date,
        description: formData.value.description,
        adjustment_amount: formData.value.adjustment_amount,
        carrying_cost: formData.value.carrying_cost,
        tds: formData.value.tds,
        general: JSON.stringify(
            formData.value.item_details.map(item => {
                const amount = Number(item.rate) * Number(item.quantity);

                const data = {
                    supplier_id: formData.value.supplier_id,
                    product_id: item.product.id,
                    is_product: 1,
                    po_number: formData.value.po_number,
                    quantity: item.quantity,
                    currency: 'BDT',
                    rate: item.rate,
                    amount: amount,
                    vat: 0,
                    vat_amount: 0,
                    discount_percent: 0,
                    discount_amount: 0,
                    total_amount: amount,
                    description: '',
                };

                // ✅ REQUIRED FOR UPDATE (UNCHANGED)
                if (!isPOCreate.value && item.id) {
                    data.id = item.id;
                }

                return data;
            })

        ),
    };

    loader.value = true;

    try {
        const res = isPOCreate.value
            ? await createNewPO(payload, getQuery())
            : await updatePOItem(payload, route.query.poId, getQuery());

        res.status ? showSuccess(res.message) : showError(res.message);
        if (res.status) navigateToListPage();
    } finally {
        loader.value = false;
    }
};

/* ================= HELPERS ================= */

const getQuery = () => {
    return `?company_id=${route.params.companyId}&page_id=${route.params.pageId}`;
}

function navigateToListPage() {
    const { poId, product_ids, ...query } = route.query;
    router.push({
        name: 'purchase-order-bill',
        params: route.params, query
    })
}

const onClickCloseButton = () => {
    router.push({ name: "purchase-order-bill", params: route.params });
};

function setFormData() {
    headerTitle.value = 'Update PO';
    supplier.value = poEditData.value.contact_profile ? poEditData.value.contact_profile.full_name: null;
    formData.value.supplier_id = poEditData.value.contact_profile_id ?? null;
    formData.value.project_id  = poEditData.value.project_id ?? null;
    formData.value.po_date     = poEditData.value.po_date;
    formData.value.po_number   = poEditData.value.po_number;
    formData.value.description = poEditData.value.description;
    formData.value.workflow_approval_logs = poEditData.value.workflow_approval_logs;
    formData.value.status = poEditData.value.status;
    formData.value.adjustment_amount = poEditData.value.adjustment_amount;
    formData.value.carrying_cost = poEditData.value.carrying_cost;
    formData.value.tds = poEditData.value.tds;
    formData.value.item_details        = [];

    for(const item of poEditData.value.purchase_general) {
        if (item.is_product === '1') {
            item.product_name = item.product.name;
            formData.value.item_details.push(item);
            continue;
        }
        item.account_head_id = item.cs_general.requisition_account.account_head_id;
        formData.value.account_details.push(item);
    }
}

async function prepareCreateUpdatePageData() {
    try {
        if(route.query.poId)
        {
            const pOResp = await fetchSinglePO(route.query.poId, getQuery());
            poEditData.value = pOResp ? pOResp.data : {};
            setFormData()
            isPOCreate.value = false;
            return;
        }
        // Generate PO number
        formData.value.po_number = await fetchPONumber(getQuery());
        formData.value.po_date = new Date().toISOString().split('T')[0];

        if (!route.query.product_ids || route.query.product_ids === 'null' || route.query.product_ids === 'undefined' || route.query.product_ids.trim() === '') return;
        const query = `?company_id=${route.params.companyId}&product_ids=${route.query.product_ids}`;
        const res = await fetchProductsByIds(query);
        if (!res.status || !res.data) return;
        formData.value.item_details = res.data.map(product => ({
            product: { id: product.id, text: product.name },
            product_id: product.id,
            product_name: product.name,
            rate: Number(product.last_purchase_rate) || 0,
            quantity: 0,
        }));
    } catch (e) {}
}

/* ================= INIT ================= */

onMounted(async () => {
    loader.value = true;

    const companyQuery = `?company_id=${route.params.companyId}`
    const projectQuery = companyQuery + '&with_donors=1';
    const contactQuery = companyQuery + '&type=supplier'
    const contactRes   = fetchContactProfiles(contactQuery)
    const projectRes = fetchProjects(projectQuery)
    const workflowQuery = getQuery() + `&approval_list_page_id=${route.params.pageId}`;

    Promise.all([
        contactRes.then(res => {
            if (res.data) contacts.value = res.data
        }),
        projectRes.then(res=> {
            if(res.data) projects.value = res.data
        }),
        fetchWorkflowFromPageId(workflowQuery).then(res => {
            if(res.data) workflowDetails.value = res.data
        }),
        prepareCreateUpdatePageData()
    ]).finally(() => {
        loader.value = false;
    })
});
</script>
